// src/services/blogService.ts
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  limit,
  startAfter,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData,
  Query,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { db, storage } from '../../../firebase/config';
import { Blog, BlogFormData } from './blog';

const BLOGS_COLLECTION = 'blogs';
const STORAGE_PATH = 'blogs';

export interface PaginatedBlogsResult {
  blogs: Blog[];
  totalCount: number;
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  firstVisible: QueryDocumentSnapshot<DocumentData> | null;
}

export interface BlogStats {
  published: number;
  drafts: number;
}

export class BlogService {
  // Generate URL-friendly slug from title
  static generateSlug(title: string): string {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // Upload background image
  static async uploadBackgroundImage(blogId: string, file: File): Promise<string> {
    try {
      const fileExtension = file.name.split('.').pop();
      const fileName = `${blogId}_${Date.now()}.${fileExtension}`;
      const storageRef = ref(storage, `${STORAGE_PATH}/${fileName}`);
      
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading background image:', error);
      throw error;
    }
  }

  // Delete background image
  static async deleteBackgroundImage(blogId: string, imageUrl: string): Promise<void> {
    try {
      const urlParts = imageUrl.split('/o/');
      if (urlParts.length < 2) return;
      
      const pathWithParams = urlParts[1];
      const filePath = decodeURIComponent(pathWithParams.split('?')[0]);
      
      const storageRef = ref(storage, filePath);
      await deleteObject(storageRef);
      
      const blogRef = doc(db, BLOGS_COLLECTION, blogId);
      await updateDoc(blogRef, {
        backgroundImageUrl: null,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error deleting background image:', error);
      throw error;
    }
  }

  /**
   * Get blog statistics (published and draft counts)
   */
  static async getBlogStats(): Promise<BlogStats> {
    try {
      const collectionRef = collection(db, BLOGS_COLLECTION);

      const publishedQuery = query(collectionRef, where('status', '==', 'published'));
      const publishedSnapshot = await getCountFromServer(publishedQuery);
      const published = publishedSnapshot.data().count;

      const draftQuery = query(collectionRef, where('status', '==', 'draft'));
      const draftSnapshot = await getCountFromServer(draftQuery);
      const drafts = draftSnapshot.data().count;

      return { published, drafts };
    } catch (error: any) {
      console.error('Error fetching blog stats:', error);
      throw error;
    }
  }

  /**
   * Get paginated blogs — uses cursor-seek so any page can be fetched
   * in any order (page 1 → page 5 → page 2 all work correctly).
   *
   * HOW IT WORKS
   * ─────────────
   * Firestore's client SDK has no offset(). The classic startAfter cursor
   * only works sequentially. To jump to page N we:
   *
   *   Step 1 — fetch the first (page-1)*pageSize doc IDs (lightweight seek).
   *            The last doc from this set becomes our startAfter cursor.
   *   Step 2 — fetch the real pageSize docs starting after that cursor.
   *
   * For page 1 the seek is skipped entirely (skip count = 0).
   */
  static async getBlogsPaginated(
    pageSize: number = 10,
    page: number = 1,
    statusFilter?: 'draft' | 'published',
    searchQuery?: string
  ): Promise<PaginatedBlogsResult> {
    try {
      const collectionRef = collection(db, BLOGS_COLLECTION);

      const whereConstraints: any[] = [];
      if (statusFilter) {
        whereConstraints.push(where('status', '==', statusFilter));
      }

      const orderConstraint = orderBy('updatedAt', 'desc');

      // ── Total count (0 doc reads) ────────────────────────────────────────
      const countSnapshot = await getCountFromServer(
        query(collectionRef, ...whereConstraints)
      );
      const totalCount = countSnapshot.data().count;

      // ── Step 1: cursor seek ───────────────────────────────────────────────
      let cursorDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      const skipCount = (page - 1) * pageSize;

      if (skipCount > 0) {
        const seekSnapshot = await getDocs(
          query(collectionRef, ...whereConstraints, orderConstraint, limit(skipCount))
        );
        if (!seekSnapshot.empty) {
          cursorDoc = seekSnapshot.docs[seekSnapshot.docs.length - 1];
        }
      }

      // ── Step 2: fetch the real page ───────────────────────────────────────
      const pageQuery: Query<DocumentData> = cursorDoc
        ? query(collectionRef, ...whereConstraints, orderConstraint, startAfter(cursorDoc), limit(pageSize))
        : query(collectionRef, ...whereConstraints, orderConstraint, limit(pageSize));

      const snapshot = await getDocs(pageQuery);

      // ── Map + client-side search filter ──────────────────────────────────
      const searchLower = searchQuery?.trim().toLowerCase() ?? '';

      const blogs: Blog[] = snapshot.docs
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate(),
            publishedAt: data.publishedAt?.toDate(),
          } as Blog;
        })
        .filter(blog => {
          if (!searchLower) return true;
          return (
            blog.title.toLowerCase().includes(searchLower) ||
            blog.authorName.toLowerCase().includes(searchLower) ||
            blog.tags.some(tag => tag.toLowerCase().includes(searchLower)) ||
            blog.paragraphs.some(p => p.content.toLowerCase().includes(searchLower))
          );
        });

      return { blogs, totalCount, lastVisible: null, firstVisible: null };
    } catch (error: any) {
      console.error('Error fetching paginated blogs:', error);
      throw error;
    }
  }

  // Create new blog
  static async createBlog(
    formData: BlogFormData,
    userId: string,
    backgroundImage?: File | null
  ): Promise<string> {
    try {
      const slug = this.generateSlug(formData.title);
      
      const blogData: any = {
        title: formData.title,
        authorName: formData.authorName,
        paragraphs: formData.paragraphs,
        tags: formData.tags,
        status: formData.status,
        slug,
        backgroundImageUrl: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
        publishedAt: formData.status === 'published' ? serverTimestamp() : null,
      };

      const docRef = await addDoc(collection(db, BLOGS_COLLECTION), blogData);
      
      if (backgroundImage) {
        const imageUrl = await this.uploadBackgroundImage(docRef.id, backgroundImage);
        await updateDoc(docRef, { backgroundImageUrl: imageUrl });
      }
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating blog:', error);
      throw error;
    }
  }

  // Update existing blog
  static async updateBlog(
    blogId: string,
    formData: BlogFormData,
    backgroundImage?: File | null,
    removeImage?: boolean
  ): Promise<void> {
    try {
      const blogRef = doc(db, BLOGS_COLLECTION, blogId);
      const blogDoc = await getDoc(blogRef);
      
      if (!blogDoc.exists()) {
        throw new Error('Blog not found');
      }

      const currentData = blogDoc.data();
      const slug = this.generateSlug(formData.title);
      
      const updateData: any = {
        title: formData.title,
        authorName: formData.authorName,
        paragraphs: formData.paragraphs,
        tags: formData.tags,
        status: formData.status,
        slug,
        updatedAt: serverTimestamp(),
      };

      if (formData.status === 'published' && currentData.status === 'draft') {
        updateData.publishedAt = serverTimestamp();
      }

      if (removeImage && currentData.backgroundImageUrl) {
        try {
          const urlParts = currentData.backgroundImageUrl.split('/o/');
          if (urlParts.length >= 2) {
            const filePath = decodeURIComponent(urlParts[1].split('?')[0]);
            await deleteObject(ref(storage, filePath));
          }
        } catch (error) {
          console.error('Error deleting old image:', error);
        }
        updateData.backgroundImageUrl = null;
      } else if (backgroundImage) {
        if (currentData.backgroundImageUrl) {
          try {
            const urlParts = currentData.backgroundImageUrl.split('/o/');
            if (urlParts.length >= 2) {
              const filePath = decodeURIComponent(urlParts[1].split('?')[0]);
              await deleteObject(ref(storage, filePath));
            }
          } catch (error) {
            console.error('Error deleting old image:', error);
          }
        }
        const imageUrl = await this.uploadBackgroundImage(blogId, backgroundImage);
        updateData.backgroundImageUrl = imageUrl;
      }

      await updateDoc(blogRef, updateData);
    } catch (error) {
      console.error('Error updating blog:', error);
      throw error;
    }
  }

  // Delete blog
  static async deleteBlog(blogId: string): Promise<void> {
    try {
      const blogRef = doc(db, BLOGS_COLLECTION, blogId);
      const blogDoc = await getDoc(blogRef);
      
      if (blogDoc.exists()) {
        const blogData = blogDoc.data();
        if (blogData.backgroundImageUrl) {
          try {
            await this.deleteBackgroundImage(blogId, blogData.backgroundImageUrl);
          } catch (error) {
            console.error('Error deleting blog image:', error);
          }
        }
      }

      await deleteDoc(blogRef);
    } catch (error) {
      console.error('Error deleting blog:', error);
      throw error;
    }
  }

  // Get all blogs (deprecated - use getBlogsPaginated instead)
  static async getBlogs(): Promise<Blog[]> {
    try {
      const querySnapshot = await getDocs(collection(db, BLOGS_COLLECTION));
      
      const blogs = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        publishedAt: doc.data().publishedAt?.toDate(),
      })) as Blog[];
      
      return blogs.sort((a, b) => {
        const dateA = a.updatedAt?.getTime() || 0;
        const dateB = b.updatedAt?.getTime() || 0;
        return dateB - dateA;
      });
    } catch (error) {
      console.error('Error fetching blogs:', error);
      throw error;
    }
  }

  // Get published blogs only
  static async getPublishedBlogs(): Promise<Blog[]> {
    try {
      const q = query(
        collection(db, BLOGS_COLLECTION),
        where('status', '==', 'published')
      );
      const querySnapshot = await getDocs(q);
      
      const blogs = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        publishedAt: doc.data().publishedAt?.toDate(),
      })) as Blog[];
      
      return blogs.sort((a, b) => {
        const dateA = a.publishedAt?.getTime() || 0;
        const dateB = b.publishedAt?.getTime() || 0;
        return dateB - dateA;
      });
    } catch (error) {
      console.error('Error fetching published blogs:', error);
      throw error;
    }
  }

  // Get draft blogs only
  static async getDraftBlogs(): Promise<Blog[]> {
    try {
      const q = query(
        collection(db, BLOGS_COLLECTION),
        where('status', '==', 'draft')
      );
      const querySnapshot = await getDocs(q);
      
      const blogs = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        publishedAt: doc.data().publishedAt?.toDate(),
      })) as Blog[];
      
      return blogs.sort((a, b) => {
        const dateA = a.updatedAt?.getTime() || 0;
        const dateB = b.updatedAt?.getTime() || 0;
        return dateB - dateA;
      });
    } catch (error) {
      console.error('Error fetching draft blogs:', error);
      throw error;
    }
  }

  // Get single blog by ID
  static async getBlog(blogId: string): Promise<Blog | null> {
    try {
      const blogRef = doc(db, BLOGS_COLLECTION, blogId);
      const blogSnap = await getDoc(blogRef);
      
      if (blogSnap.exists()) {
        const data = blogSnap.data();
        return {
          id: blogSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
          publishedAt: data.publishedAt?.toDate(),
        } as Blog;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching blog:', error);
      throw error;
    }
  }

  // Get blog by slug
  static async getBlogBySlug(slug: string): Promise<Blog | null> {
    try {
      const q = query(
        collection(db, BLOGS_COLLECTION),
        where('slug', '==', slug)
      );
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) return null;
      
      const doc = querySnapshot.docs[0];
      const data = doc.data();
      
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
        publishedAt: data.publishedAt?.toDate(),
      } as Blog;
    } catch (error) {
      console.error('Error fetching blog by slug:', error);
      throw error;
    }
  }

  // Toggle blog status
  static async toggleBlogStatus(blogId: string, status: 'draft' | 'published'): Promise<void> {
    try {
      const blogRef = doc(db, BLOGS_COLLECTION, blogId);
      const updateData: any = {
        status,
        updatedAt: serverTimestamp(),
      };
      
      if (status === 'published') {
        const blogDoc = await getDoc(blogRef);
        if (blogDoc.exists() && !blogDoc.data().publishedAt) {
          updateData.publishedAt = serverTimestamp();
        }
      }
      
      await updateDoc(blogRef, updateData);
    } catch (error) {
      console.error('Error toggling blog status:', error);
      throw error;
    }
  }
}